<template>
  <div class="relative">
    <img
      v-show="curIndex !== 0 && noOfItems > 3"
      aria-label="Previous"
      class="absolute top-[45%] left-[-48px] w-[32px] h-[32px] cursor-pointer hidden lg:block"
      :class="`${title}-prev`"
      src="/assets/icons/playback-resources/chevron-left--black.svg"
      alt=""
    />

    <img
      v-show="curIndex !== noOfItems - 1 && noOfItems > 3"
      aria-label="Next"
      class="absolute top-[45%] right-[-48px] w-[32px] h-[32px] cursor-pointer hidden lg:block"
      :class="`${title}-next`"
      src="/assets/icons/playback-resources/chevron-right--black.svg"
      alt=""
    />
    <div :id="title" :ref="title" class="scrollbar-hide relative">
      <slot></slot>
    </div>
    <div v-if="showDots" :class="`${title}-dots`" class="carousel-dots"></div>
  </div>
</template>

<script>
import Glider from 'glider-js';

import 'glider-js/glider.css';

export default {
  props: {
    itemWidth: {
      type: Number,
      default: 320,
    },
    mobileItemWidth: {
      type: Number,
      default: 280,
    },
    title: {
      type: String,
      default: 'glider',
    },
    noOfItems: {
      type: Number,
      default: 0,
    },
    showDots: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      glider: null,
      curIndex: 0,
    };
  },
  mounted() {
    this.initGlider();
  },
  beforeUnmount() {
    // Remove Glider's window 'resize' listener so the detached instance can be
    // GC'd when the carousel is remounted (e.g. on conference switch). We avoid
    // glider.destroy() here because it does a parentNode.replaceChild that
    // conflicts with Vue's own DOM teardown and can throw.
    if (this.glider && this.glider.resize) {
      window.removeEventListener('resize', this.glider.resize);
      this.glider = null;
    }
  },
  watch: {
    // Safety net: if items change without a full remount (e.g. My List updates,
    // or a recycled instance receives a new conference's items), re-scan slides
    // so Glider tracks the new DOM instead of leaving items stacked vertically.
    noOfItems() {
      this.$nextTick(() => {
        if (this.glider) {
          this.glider.refresh(true);
        } else {
          this.initGlider();
        }
      });
    },
  },
  methods: {
    initGlider() {
      const el = this.$refs[`${this.title}`];
      if (!el) {
        return;
      }
      this.glider = new Glider(el, {
        slidesToShow: 'auto',
        slidesToScroll: 1,
        scrollLock: true,
        draggable: true,
        itemWidth: this.mobileItemWidth,
        exactWidth: true,
        arrows: {
          prev: `.${this.title}-prev`,
          next: `.${this.title}-next`,
        },
        dots: this.showDots ? `.${this.title}-dots` : undefined,
        responsive: [
          {
            // screens greater than >= 775px
            breakpoint: 768,
            settings: {
              itemWidth: this.itemWidth,
            },
          },
        ],
      });
      this.initListenOnSlide();
    },
    initListenOnSlide() {
      this.$refs[`${this.title}`].addEventListener('glider-slide-visible', ({ detail }) => {
        this.curIndex = detail.slide;
      });
    },
    scrollToItem(index) {
      this.glider.scrollItem(index);
    },
    // Ionic caches pages, so returning via ionViewWillEnter re-shows this
    // component without remounting — call this from the page hook so Glider
    // re-measures instead of keeping stale widths / stacked slides.
    refresh() {
      if (this.glider) {
        this.glider.refresh(true);
      } else {
        this.initGlider();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Glider renders the dot buttons itself, so they can't take Tailwind classes */
.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;

  :deep(.glider-dot) {
    width: 8px;
    height: 8px;
    margin: 0;
    border-radius: 9999px;
    background: #d9d9d9;
    transition: all 0.2s ease;

    &.active {
      width: 16px;
      background: #00a9e0;
    }
  }
}
</style>
